import { supabase } from '../supabaseClient';
import { logAudit } from './auditLog';

/**
 * 建立新 Project
 */
export const createProject = async (data) => {
  try {
    const insertData = {
      company_id: data.company_id,
      department_id: data.department_id,
      owner_id: data.owner_id,
      name: data.name,
      description: data.description || null,
      color: data.color || '#667eea',
      icon: data.icon || null,
      status: data.status || 'active',
      external_url: data.external_url || null,
      milestone_start_date: data.milestone_start_date || null,
      milestone_end_date: data.milestone_end_date || null,
      autopilot_config: data.autopilot_config || null,
    };

    const { data: project, error } = await supabase
      .from('projects')
      .insert(insertData)
      .select('*')
      .single();

    if (error) throw error;

    await logAudit('create', 'project', project.id, project.name, {
      after: project,
    });

    return { data: project, error: null };
  } catch (error) {
    console.error('Error creating project:', error);
    return { data: null, error };
  }
};

/**
 * 取得同公司所有 active 的 Projects
 * Project 是公司層級的共享容器，同公司的人都能看到全部 project。
 * @param {object} options - { companyId, status }
 */
export const getProjects = async (options = {}) => {
  try {
    const companyId = options.companyId;
    if (!companyId) return { data: [], error: new Error('companyId is required') };

    let query = supabase
      .from('projects')
      .select('*')
      .eq('company_id', companyId)
      .order('sort_order', { ascending: true })
      .order('name', { ascending: true });

    // Filter by status (default: active)
    if (options.status) {
      query = query.eq('status', options.status);
    } else {
      query = query.eq('status', 'active');
    }

    const { data, error } = await query;
    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching projects:', error);
    return { data: null, error };
  }
};

/**
 * 取得單一 Project
 */
export const getProject = async (projectId) => {
  try {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', projectId)
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching project:', error);
    return { data: null, error };
  }
};

/**
 * 更新 Project
 */
export const updateProject = async (projectId, updates) => {
  try {
    const { data: beforeData } = await supabase
      .from('projects')
      .select('*')
      .eq('id', projectId)
      .single();

    const updateData = {};
    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.color !== undefined) updateData.color = updates.color;
    if (updates.icon !== undefined) updateData.icon = updates.icon;
    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.external_url !== undefined) updateData.external_url = updates.external_url || null;
    if (updates.milestone_start_date !== undefined) updateData.milestone_start_date = updates.milestone_start_date || null;
    if (updates.milestone_end_date !== undefined) updateData.milestone_end_date = updates.milestone_end_date || null;
    if (updates.autopilot_config !== undefined) updateData.autopilot_config = updates.autopilot_config || null;
    updateData.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .from('projects')
      .update(updateData)
      .eq('id', projectId)
      .select()
      .single();

    if (error) throw error;

    await logAudit('update', 'project', projectId, data.name, {
      before: beforeData,
      after: data,
    });

    return { data, error: null };
  } catch (error) {
    console.error('Error updating project:', error);
    return { data: null, error };
  }
};

/**
 * 刪除（封存）Project
 */
export const archiveProject = async (projectId) => {
  return updateProject(projectId, { status: 'archived' });
};

/**
 * 刪除 Project（永久）
 */
export const deleteProject = async (projectId) => {
  try {
    const { data: projectData } = await supabase
      .from('projects')
      .select('name, company_id, department_id')
      .eq('id', projectId)
      .single();

    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', projectId);

    if (error) throw error;

    await logAudit('delete', 'project', projectId, projectData?.name, {
      before: projectData,
    });

    return { error: null };
  } catch (error) {
    console.error('Error deleting project:', error);
    return { error };
  }
};
